/**
 * PMRadar — cross-property preventive maintenance dashboard.
 * Route: `/admin/radar/pm`. Sorts every active PM task by soonest-due
 * across every property. Filter chips: All / Overdue / Due this week.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Warning, Clock, ArrowRight } from "@phosphor-icons/react";

export default function PMRadar() {
  const [data, setData] = useState({ tasks: [], count: 0, overdue: 0, due_this_week: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [within, setWithin] = useState(60);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pm-radar", { params: { within_days: within } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to load PM radar"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [within]);

  const filtered = data.tasks.filter((t) => {
    const d = t.days_until_due;
    if (filter === "overdue") return d < 0;
    if (filter === "week") return d >= 0 && d <= 7;
    if (filter === "month") return d >= 0 && d <= 30;
    return true;
  });

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="pm-radar-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Wrench weight="fill" size={32} className="text-emerald-600"/>
          <div>
            <h1 className="font-display font-black text-3xl">Preventive Maintenance Radar</h1>
            <p className="text-sm text-slate-500">Cyclic tasks across every property. Cron auto-drafts WOs 14 days out.</p>
          </div>
        </div>

        {/* Summary chips */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button onClick={() => setFilter("all")}
                  data-testid="pm-filter-all"
                  className={`rugged-card p-3 text-left border-2 ${filter === "all" ? "border-emerald-500 shadow-[3px_3px_0_#0f172a]" : "border-slate-200"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">All in Radar</div>
            <div className="text-2xl font-black text-slate-800 mt-1">{data.count}</div>
          </button>
          <button onClick={() => setFilter("overdue")}
                  data-testid="pm-filter-overdue"
                  className={`rugged-card p-3 text-left border-2 ${filter === "overdue" ? "border-red-500 shadow-[3px_3px_0_#0f172a]" : "border-slate-200"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Overdue</div>
            <div className="text-2xl font-black text-red-700 mt-1">{data.overdue}</div>
          </button>
          <button onClick={() => setFilter("week")}
                  data-testid="pm-filter-week"
                  className={`rugged-card p-3 text-left border-2 ${filter === "week" ? "border-yellow-500 shadow-[3px_3px_0_#0f172a]" : "border-slate-200"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-yellow-800">Due This Week</div>
            <div className="text-2xl font-black text-yellow-700 mt-1">{data.due_this_week}</div>
          </button>
          <button onClick={() => setFilter("month")}
                  data-testid="pm-filter-month"
                  className={`rugged-card p-3 text-left border-2 ${filter === "month" ? "border-cyan-500 shadow-[3px_3px_0_#0f172a]" : "border-slate-200"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Due This Month</div>
            <div className="text-2xl font-black text-cyan-700 mt-1">
              {data.tasks.filter((t) => t.days_until_due >= 0 && t.days_until_due <= 30).length}
            </div>
          </button>
        </div>

        {/* Horizon */}
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="font-black uppercase tracking-widest text-slate-500">Horizon:</span>
          {[30, 60, 90, 180].map((d) => (
            <button key={d} onClick={() => setWithin(d)}
                    data-testid={`pm-horizon-${d}`}
                    className={`h-8 px-3 border-2 font-black uppercase text-[10px] tracking-widest
                      ${within === d ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300"}`}>
              {d} DAYS
            </button>
          ))}
        </div>

        {/* Task list */}
        <div className="mt-5 rugged-card p-0 overflow-hidden">
          {loading && <div className="p-6 text-slate-500 font-bold">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-center" data-testid="pm-radar-empty">
              <Wrench size={40} className="mx-auto text-slate-300 mb-2"/>
              <div className="font-black text-slate-500">No matching tasks in the {within}-day window.</div>
              <div className="text-xs text-slate-400 mt-1">Nice work keeping ahead of it.</div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="divide-y divide-slate-200">
              {filtered.map((t) => {
                const d = t.days_until_due;
                const chipCls =
                  d < 0 ? "bg-red-100 text-red-800 border-red-500"
                  : d <= 7 ? "bg-yellow-100 text-yellow-800 border-yellow-500"
                  : d <= 30 ? "bg-cyan-100 text-cyan-800 border-cyan-500"
                  : "bg-emerald-50 text-emerald-700 border-emerald-300";
                return (
                  <div key={t.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`pm-radar-row-${t.id}`}>
                    <Warning weight={d < 0 ? "fill" : "regular"} size={20}
                             className={d < 0 ? "text-red-600" : "text-slate-400"}/>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm truncate">{t.task_label}</div>
                      <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                        <Link to={`/admin/properties/${encodeURIComponent(t.address)}`}
                              className="hover:underline font-bold text-slate-700">
                          {t.address}{t.unit ? ` · ${t.unit}` : ""}
                        </Link>
                        <span>· every {t.cadence_months}mo</span>
                        {t.vendor_name && <span>· <b>{t.vendor_name}</b></span>}
                        {t.auto_wo_id && (
                          <Link to={`/admin/jobs/${t.auto_wo_id}`}
                                className="text-emerald-700 font-black hover:underline">
                            · WO drafted →
                          </Link>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 border-2 font-black uppercase text-[10px] tracking-widest whitespace-nowrap ${chipCls}`}>
                      {d < 0 ? `${Math.abs(d)}d OVERDUE` : `${d}d`}
                    </span>
                    <Link to={`/admin/properties/${encodeURIComponent(t.address)}`}
                          className="h-8 w-8 flex items-center justify-center border-2 border-slate-300 hover:border-emerald-500 hover:bg-emerald-50">
                      <ArrowRight weight="bold" size={14}/>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
